import { Controller } from '@hotwired/stimulus'

// Seller variant builder: tap many sizes + colours, one stock, generate all combinations.
export default class extends Controller {
  static targets = [
    'rows',
    'sizeGroupTemplate',
    'committedVariantTemplate',
    'builder',
    'sizeField',
    'sizeChip',
    'sizeChipSet',
    'customSizeInput',
    'customSizeChips',
    'customSizeChipTemplate',
    'colorChip',
    'colorChipGrid',
    'customColorInput',
    'customColorChips',
    'customColorChipTemplate',
    'bulkStock',
    'bulkPrice',
    'generateCount',
    'commitButton',
    'commitLabel',
    'emptyCommitted',
    'builderTitle',
    'builderHint'
  ]

  static values = {
    variantConfig: Object,
    variantSections: Object,
    colorChips: Array,
    colorSwatches: Object,
    sizeChips: Object
  }

  connect () {
    this.showSizes = false
    this.showColors = true
    this.currentSlug = ''
    this.onCategoryUpdate = this.onCategoryUpdate.bind(this)
    document.addEventListener('product-category-fields:updated', this.onCategoryUpdate)
    this.syncFromParentSlug()
    this.updateEmptyState()
    this.updateGenerateCount()
  }

  disconnect () {
    document.removeEventListener('product-category-fields:updated', this.onCategoryUpdate)
  }

  syncFromParentSlug () {
    const parent = this.element.closest('[data-controller*="product-category-fields"]')
    if (!parent) return

    const select = parent.querySelector('[data-product-category-fields-target="select"]')
    let slugMap = {}
    try {
      slugMap = JSON.parse(parent.dataset.productCategoryFieldsSlugMapValue || '{}')
    } catch (_e) { /* ignore */ }

    const slug = select?.value ? (slugMap[select.value] || 'general') : ''
    const config = this.variantConfigValue?.[slug] || { sizes: false, colors: false }
    this.applyCategory(slug, config)
  }

  onCategoryUpdate (event) {
    const { slug, config } = event.detail || {}
    this.applyCategory(slug || '', config || { sizes: false, colors: false })
  }

  applyCategory (slug, config) {
    const nextSlug = slug || ''
    const slugChanged = nextSlug !== this.currentSlug
    this.currentSlug = nextSlug
    this.showSizes = Boolean(config?.sizes)
    this.showColors = Boolean(config?.colors)

    // Only clear in-progress taps when the shelf/category actually changes.
    if (slugChanged) this.resetBuilder()

    this.syncBuilderVisibility()
    this.syncSizeChipSets()
    this.updateCommitCopy()
    this.updateGenerateCount()
  }

  syncBuilderVisibility () {
    if (this.hasSizeFieldTarget) this.sizeFieldTarget.classList.toggle('hidden', !this.showSizes)
    if (this.hasBuilderTarget) this.builderTarget.classList.toggle('hidden', !this.showSizes && !this.showColors)

    this.element.querySelectorAll('[data-variant-color-column]').forEach((col) => {
      col.classList.toggle('hidden', !this.showColors)
    })
  }

  syncSizeChipSets () {
    if (!this.hasSizeChipSetTarget) return

    let matched = false
    this.sizeChipSetTargets.forEach((set) => {
      const show = set.dataset.sizeChipSet === this.currentSlug
      set.classList.toggle('hidden', !show)
      if (show) matched = true
    })

    // Fallback: if shelf supports sizes but has no dedicated chip list, show first set.
    if (this.showSizes && !matched && this.sizeChipSetTargets[0]) {
      this.sizeChipSetTargets[0].classList.remove('hidden')
    }
  }

  updateCommitCopy () {
    const section = this.variantSectionsValue?.[this.currentSlug] || {}
    if (this.hasCommitLabelTarget) this.commitLabelTarget.textContent = section.add_label || 'Generate options'
    if (this.hasBuilderTitleTarget) {
      this.builderTitleTarget.textContent = this.showSizes && this.showColors ? '1 · Tap sizes' : 'Tap colours'
    }
    if (this.hasBuilderHintTarget) {
      this.builderHintTarget.textContent = this.showSizes && this.showColors
        ? 'Select every size and colour you sell. One stock number applies to all.'
        : 'Select every colour you sell. One stock number applies to all.'
    }
    this.element.querySelectorAll('[data-builder-color-heading]').forEach((el) => {
      el.textContent = this.showSizes ? '2 · Tap colours' : 'Tap colours'
    })
  }

  toggleSizeChip (event) {
    event.preventDefault()
    event.stopPropagation()
    this.togglePressedChip(event.currentTarget)
    this.updateGenerateCount()
  }

  addCustomSize (event) {
    event.preventDefault()
    if (!this.hasCustomSizeInputTarget || !this.hasCustomSizeChipsTarget) return

    const sizes = this.splitSizeTokens(this.customSizeInputTarget.value)
    if (sizes.length === 0) {
      window.alert('Type a size first (e.g. 32, or 32, 34, 36).')
      return
    }

    let added = 0
    sizes.forEach((size) => {
      if (this.sizeAlreadySelected(size)) return
      this.appendCustomSizeChip(size)
      added += 1
    })

    if (added === 0) {
      window.alert('Those sizes are already selected.')
      return
    }

    this.customSizeInputTarget.value = ''
    this.updateGenerateCount()
  }

  appendCustomSizeChip (size) {
    if (!this.hasCustomSizeChipTemplateTarget) return
    this.customSizeChipsTarget.insertAdjacentHTML('beforeend', this.customSizeChipTemplateTarget.innerHTML)
    const chip = this.customSizeChipsTarget.lastElementChild
    chip.dataset.size = size
    const label = chip.querySelector('[data-size-label]')
    if (label) label.textContent = size
    this.paintChip(chip, true)
  }

  splitSizeTokens (raw) {
    return String(raw || '')
      .split(/[,/|]+/)
      .map((value) => value.trim())
      .filter(Boolean)
  }

  toggleColorChip (event) {
    event.preventDefault()
    event.stopPropagation()
    this.togglePressedChip(event.currentTarget)
    this.updateGenerateCount()
  }

  addCustomColor (event) {
    event.preventDefault()
    if (!this.hasCustomColorInputTarget || !this.hasCustomColorChipsTarget) return

    const color = this.customColorInputTarget.value.trim()
    if (!color) {
      window.alert('Type a colour name first.')
      return
    }
    if (this.colorAlreadySelected(color)) {
      window.alert('That colour is already selected.')
      return
    }

    const swatch = this.swatchFor(color)
    if (!this.hasCustomColorChipTemplateTarget) return
    this.customColorChipsTarget.insertAdjacentHTML('beforeend', this.customColorChipTemplateTarget.innerHTML)
    const chip = this.customColorChipsTarget.lastElementChild
    chip.dataset.color = color
    chip.dataset.swatch = swatch
    const label = chip.querySelector('[data-color-label]')
    if (label) label.textContent = color
    const swatchEl = chip.querySelector('[data-color-swatch]')
    if (swatchEl) swatchEl.style.backgroundColor = swatch
    this.customColorInputTarget.value = ''
    this.paintChip(chip, true)
    this.updateGenerateCount()
  }

  togglePressedChip (chip) {
    if (!chip) return
    const next = chip.getAttribute('aria-pressed') !== 'true'
    this.paintChip(chip, next)
  }

  paintChip (chip, selected) {
    chip.setAttribute('aria-pressed', selected ? 'true' : 'false')
    chip.dataset.selected = selected ? 'true' : 'false'
    chip.classList.toggle('border-shop-500', selected)
    chip.classList.toggle('bg-shop-50', selected)
    chip.classList.toggle('text-shop-900', selected)
    chip.classList.toggle('ring-2', selected)
    chip.classList.toggle('ring-shop-200', selected)
    chip.classList.toggle('border-slate-200', !selected)
    chip.classList.toggle('bg-white', !selected)
    chip.classList.toggle('text-slate-700', !selected)
  }

  selectedSizes () {
    return this.pressedChips(this.sizeChipElements())
      .map((chip) => (chip.dataset.size || '').trim())
      .filter(Boolean)
  }

  selectedColors () {
    return this.pressedChips(this.colorChipElements())
      .map((chip) => (chip.dataset.color || '').trim())
      .filter(Boolean)
  }

  sizeChipElements () {
    return [
      ...this.element.querySelectorAll('[data-size-fields-target="sizeChip"]'),
      ...this.element.querySelectorAll('[data-custom-size]')
    ]
  }

  colorChipElements () {
    return [
      ...this.element.querySelectorAll('[data-size-fields-target="colorChip"]'),
      ...this.element.querySelectorAll('[data-custom-color]')
    ]
  }

  pressedChips (chips) {
    return Array.from(chips).filter((chip) => {
      if (chip.getAttribute('aria-pressed') !== 'true') return false
      // Ignore chips in inactive size lists only — not every ancestor .hidden
      // (the whole builder used to live under a hidden field wrapper).
      const set = chip.closest('[data-size-chip-set]')
      if (set?.classList.contains('hidden')) return false
      return true
    })
  }

  sizeAlreadySelected (size) {
    const key = size.toLowerCase()
    return this.selectedSizes().some((value) => value.toLowerCase() === key)
  }

  colorAlreadySelected (color) {
    const key = color.toLowerCase()
    return this.selectedColors().some((value) => value.toLowerCase() === key)
  }

  updateGenerateCount () {
    if (!this.hasGenerateCountTarget) return

    const sizeCount = this.showSizes ? this.selectedSizes().length : 0
    const colorCount = this.showColors ? this.selectedColors().length : 0
    const total = (this.showSizes ? sizeCount : 1) * (this.showColors ? colorCount : 1)

    if ((this.showSizes && sizeCount === 0) || (this.showColors && colorCount === 0)) {
      this.generateCountTarget.textContent = this.showSizes
        ? 'Select sizes and colours to see how many options will be created.'
        : 'Select colours to see how many options will be created.'
      return
    }

    if (this.showSizes && this.showColors) {
      this.generateCountTarget.textContent = `${sizeCount} sizes × ${colorCount} colours = ${total} options`
    } else if (this.showColors) {
      this.generateCountTarget.textContent = `${colorCount} colour${colorCount === 1 ? '' : 's'} will be added`
    } else {
      this.generateCountTarget.textContent = `${sizeCount} size${sizeCount === 1 ? '' : 's'} will be added`
    }
  }

  commitBatch (event) {
    event.preventDefault()

    const sizes = this.showSizes ? this.selectedSizes() : ['']
    const colors = this.showColors ? this.selectedColors() : ['']
    const stockRaw = this.hasBulkStockTarget ? this.bulkStockTarget.value.trim() : ''
    const stock = parseInt(stockRaw, 10)
    const priceCents = this.hasBulkPriceTarget ? this.parsePriceToCents(this.bulkPriceTarget.value) : null

    if (this.showSizes && this.selectedSizes().length === 0) {
      window.alert('Tap at least one size, or add a custom size.')
      return
    }
    if (this.showColors && this.selectedColors().length === 0) {
      window.alert('Tap at least one colour, or add a custom colour.')
      return
    }
    if (stockRaw === '' || Number.isNaN(stock) || stock < 0) {
      window.alert('Enter stock for each option (0 or more).')
      this.bulkStockTarget?.focus()
      return
    }

    let added = 0
    const skipped = []

    sizes.forEach((size) => {
      colors.forEach((color) => {
        if (this.committedVariantExists(size, color)) {
          skipped.push(this.showSizes ? `${size} / ${color}` : color)
          return
        }
        this.appendCommittedVariant({
          size_label: size,
          color,
          stock_quantity: stock,
          price_override_cents: priceCents
        })
        added += 1
      })
    })

    if (added === 0) {
      window.alert(skipped.length ? `Already added: ${skipped.join(', ')}` : 'Nothing to add.')
      return
    }
    if (skipped.length) {
      window.alert(`Added ${added} option(s). Skipped duplicates: ${skipped.slice(0, 8).join(', ')}${skipped.length > 8 ? '…' : ''}`)
    }

    this.resetBuilder()
    this.updateEmptyState()
    this.updateGenerateCount()
  }

  sizeGroupKey (size) {
    return (size || '').trim()
  }

  findSizeGroup (size) {
    const key = this.sizeGroupKey(size)
    return this.rowsTarget.querySelector(`[data-committed-size-group][data-size="${CSS.escape(key)}"]`)
  }

  ensureSizeGroup (size) {
    let group = this.findSizeGroup(size)
    if (group) return group.querySelector('[data-committed-colors]')

    this.rowsTarget.insertAdjacentHTML('beforeend', this.sizeGroupTemplateTarget.innerHTML)
    group = this.rowsTarget.lastElementChild
    const key = this.sizeGroupKey(size)
    group.dataset.size = key
    const label = group.querySelector('[data-size-group-label]')
    if (label) label.textContent = key ? `Size ${key}` : 'Colours'
    return group.querySelector('[data-committed-colors]')
  }

  committedVariantExists (size, color) {
    const group = this.findSizeGroup(size)
    if (!group) return false
    const key = color.toLowerCase()
    return Array.from(group.querySelectorAll('[data-committed-variant]'))
      .filter((el) => !el.classList.contains('hidden'))
      .some((el) => el.dataset.color === key)
  }

  appendCommittedVariant (attrs) {
    const container = this.ensureSizeGroup(attrs.size_label || '')
    const html = this.committedVariantTemplateTarget.innerHTML.replace(/NEW_RECORD/g, this.uniqueId())
    container.insertAdjacentHTML('beforeend', html)

    const chip = container.lastElementChild
    chip.dataset.color = (attrs.color || '').toLowerCase()

    const sizeInput = chip.querySelector('input[name*="[size_label]"]')
    const colorInput = chip.querySelector('input[name*="[color]"]')
    const stockInput = chip.querySelector('input[name*="[stock_quantity]"]')
    const priceInput = chip.querySelector('input[name*="[price_override_cents]"]')

    if (sizeInput) sizeInput.value = attrs.size_label || ''
    if (colorInput) colorInput.value = attrs.color || ''
    if (stockInput) stockInput.value = attrs.stock_quantity ?? 0
    if (priceInput) priceInput.value = attrs.price_override_cents ?? ''

    const label = chip.querySelector('[data-variant-color-label]')
    if (label) label.textContent = attrs.color || 'Option'
    const swatchEl = chip.querySelector('[data-variant-swatch]')
    if (swatchEl) swatchEl.style.backgroundColor = this.swatchFor(attrs.color)
  }

  parsePriceToCents (raw) {
    if (!raw || !String(raw).trim()) return null
    const n = parseFloat(String(raw).replace(/,/g, ''))
    if (Number.isNaN(n) || n < 0) return null
    return Math.round(n * 100)
  }

  removeVariant (event) {
    event.preventDefault()
    const chip = event.currentTarget.closest('[data-committed-variant]')
    if (!chip) return

    const destroyField = chip.querySelector('input[data-destroy-flag]')
    if (destroyField) {
      destroyField.value = '1'
      chip.classList.add('hidden')
    } else {
      chip.remove()
    }

    this.cleanupEmptyGroups()
    this.updateEmptyState()
  }

  removeSizeGroup (event) {
    event.preventDefault()
    const group = event.currentTarget.closest('[data-committed-size-group]')
    if (!group) return

    group.querySelectorAll('[data-committed-variant]').forEach((chip) => {
      const destroyField = chip.querySelector('input[data-destroy-flag]')
      if (destroyField) {
        destroyField.value = '1'
        chip.classList.add('hidden')
      } else {
        chip.remove()
      }
    })

    group.classList.add('hidden')
    this.updateEmptyState()
  }

  cleanupEmptyGroups () {
    this.rowsTarget.querySelectorAll('[data-committed-size-group]').forEach((group) => {
      const visible = group.querySelectorAll('[data-committed-variant]:not(.hidden)')
      if (visible.length === 0) group.classList.add('hidden')
    })
  }

  resetBuilder () {
    this.sizeChipElements().concat(this.colorChipElements()).forEach((chip) => this.paintChip(chip, false))
    if (this.hasCustomSizeChipsTarget) this.customSizeChipsTarget.innerHTML = ''
    if (this.hasCustomColorChipsTarget) this.customColorChipsTarget.innerHTML = ''
    if (this.hasCustomSizeInputTarget) this.customSizeInputTarget.value = ''
    if (this.hasCustomColorInputTarget) this.customColorInputTarget.value = ''
  }

  visibleSizeGroups () {
    return Array.from(this.rowsTarget.querySelectorAll('[data-committed-size-group]'))
      .filter((group) => !group.classList.contains('hidden') &&
        group.querySelectorAll('[data-committed-variant]:not(.hidden)').length > 0)
  }

  updateEmptyState () {
    if (!this.hasEmptyCommittedTarget) return
    this.emptyCommittedTarget.classList.toggle('hidden', this.visibleSizeGroups().length > 0)
  }

  swatchFor (color) {
    const map = this.colorSwatchesValue || {}
    const hit = Object.entries(map).find(([name]) => name.toLowerCase() === String(color || '').toLowerCase())
    return hit ? hit[1] : '#94a3b8'
  }

  uniqueId () {
    return `${Date.now()}${Math.floor(Math.random() * 1000)}`
  }
}
